import React from "react";
import Logo from "./Logo";
import Reveal from "./Reveal";
import { brand, footer } from "../mock";
import { Mail } from "lucide-react";

/**
 * Footer — balanced three-column layout (~40% / 25% / 35%).
 *  Left:   Brand (logo + tagline)
 *  Middle: Explore (nav anchors)
 *  Right:  Contact (mailto + copy-to-clipboard)
 *
 * The visible top of the logo is aligned with the "Explore" and "Contact"
 * headings. A small negative margin compensates for the transparent padding
 * baked into the logo artwork so headings and glyphs sit on the same optical
 * baseline.
 *
 * Responsive behaviour:
 *  - Mobile: columns stack as Brand → Explore → Contact, each left-aligned
 *    with 24px side padding and 28–32px vertical gaps. The copy of the
 *    copyright strip stacks naturally below its hairline divider.
 *  - All interactive elements have a visible keyboard focus ring (brand
 *    purple) and are at least 40px tall on touch — easy and precise to tap.
 */
const Footer = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer id="contact" className="bg-white border-t border-neutral-200/70">
      <div
        className="max-w-[1200px] mx-auto px-6 md:px-10 pt-12 pb-10 md:pt-16 md:pb-10"
        style={{
          // Desktop padding mirrors the previously-approved 64 / 40 rhythm.
          // Mobile tightens to 48 / 40 so the section doesn't feel oversized
          // on small screens.
        }}
      >
        {/* Columns: stack vertically on mobile (Brand → Explore → Contact)
            with ~32px gaps; switch to the 40/25/35 track grid at md. */}
        <div className="grid grid-cols-1 md:grid-cols-[40fr_25fr_35fr] gap-y-8 sm:gap-y-9 md:gap-x-10 md:gap-y-0 items-start">
          {/* Column 1 — Brand */}
          <Reveal y={16} delay={0}>
            {/* -ml / -mt offsets compensate for the transparent padding baked
                into the logo artwork so the brand's optical left edge aligns
                with the container and its visible top aligns with the
                headings on the right (desktop only). */}
            <div className="-mt-1 md:-mt-2 -ml-1">
              <Logo size={76} />
            </div>
            <p className="mt-3 max-w-[360px] font-['Montserrat'] font-normal text-neutral-500 text-sm md:text-[15px] leading-relaxed">
              {brand.tagline}
            </p>
          </Reveal>

          {/* Column 2 — Explore */}
          <Reveal y={16} delay={120}>
            <nav aria-label="Explore">
              <h4 className="font-['Montserrat'] font-semibold text-neutral-900 text-base md:text-lg mb-4 md:mb-6">
                Explore
              </h4>
              <ul className="space-y-2.5 md:space-y-3">
                {footer.companyLinks.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollTo(l.href);
                      }}
                      className="inline-flex items-center min-h-[40px] md:min-h-0 font-['Montserrat'] font-normal text-neutral-500 hover:text-[#7B3FA7] focus-visible:outline-none focus-visible:text-[#7B3FA7] focus-visible:underline focus-visible:underline-offset-4 focus-visible:decoration-[#7B3FA7]/60 rounded transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </Reveal>

          {/* Column 3 — Contact */}
          <Reveal y={16} delay={240}>
            <div>
              <h4 className="font-['Montserrat'] font-semibold text-neutral-900 text-base md:text-lg mb-4 md:mb-6">
                Contact
              </h4>
              {/* Mailto link with envelope glyph. */}
              <div className="flex items-center">
                <a
                  href={`mailto:${brand.email}`}
                  data-testid="footer-email-link"
                  className="group inline-flex items-center gap-3 min-h-[40px] font-['Montserrat'] font-normal text-neutral-700 hover:text-[#7B3FA7] focus-visible:outline-none focus-visible:text-[#7B3FA7] focus-visible:ring-2 focus-visible:ring-[#7B3FA7]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded-full transition-colors break-all"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-full bg-[#F4EBFB] text-[#7B3FA7] group-hover:bg-[#7B3FA7] group-hover:text-white transition-colors">
                    <Mail size={14} />
                  </span>
                  <span className="text-[15px] md:text-base">{brand.email}</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Bottom strip — fine divider, 32px above / 24px below. Stacks
            naturally on mobile and sits comfortably on one line on desktop. */}
        <Reveal y={12} delay={360}>
          <div className="mt-8 md:mt-8">
            <div className="border-t border-neutral-200/70" />
            <div className="pt-5 md:pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
              <p className="font-['Montserrat'] text-[13px] md:text-[14px] text-neutral-500 leading-relaxed">
                {footer.copyright}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </footer>
  );
};

export default Footer;
